import { memo, startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';

import type { SxProps, Theme } from '@mui/material/styles';

import { csvTableOptions } from '@/components/CsvTableWidget/api/csvTable.options';
import type {
  CsvTableData,
  WorkerMessage
} from '@/components/CsvTableWidget/api/csvTable.transform';
import CsvTableWorker from '@/components/CsvTableWidget/api/csvTable.worker?worker';
import { EmptyState } from '@/components/EmptyState';
import Loader from '@/components/Loader';
import TimeRangeButtons from '@/components/TimeRangeButtons/TimeRangeButtons';
import type { TimeRangeButton } from '@/constants/constants';
import { TABLE_ROW_HEIGHT_PX } from '@/constants/constants';
import { Z_INDEX } from '@/constants/z-index';
import { Permission } from '@/entities/permissions';
import { useAppSelector } from '@/hooks/redux-hooks';
import useCsvDownload from '@/hooks/useCsvDownload';
import usePermission from '@/shared/hooks/usePermission';
import type { DateFormattingConfig } from '@/utils/parseUserDateString';

const TABLE_HEADER_HEIGHT_PX = 47;

const relativeWrapSx: SxProps<Theme> = { position: 'relative' };

const overlaySx: SxProps<Theme> = {
  alignItems: 'center',
  backgroundColor: 'rgba(0,0,0,0.25)',
  bottom: 0,
  display: 'flex',
  justifyContent: 'center',
  left: 0,
  position: 'absolute',
  right: 0,
  top: 0,
  zIndex: Z_INDEX.raised
};

const tableContainerBaseSx: SxProps<Theme> = {
  pb: 2,
  borderCollapse: 'separate',
  borderSpacing: 0
};

const tableSx: SxProps<Theme> = { tableLayout: 'auto' };

const tableHeadSx: SxProps<Theme> = theme => ({
  '& th': {
    px: 1.5,
    pb: 1,
    borderBottom: `0.5px solid ${theme.palette.text.primary}`,
    backgroundColor: 'background.paper'
  }
});

const tableRowSx: SxProps<Theme> = {
  '& td': { px: 1.5 },
  '&[data-striped="true"]': { backgroundColor: '#121836' }
};

const spacerCellSx: SxProps<Theme> = { height: 0, p: 0, border: 0 };

const nowrapCellSx: SxProps<Theme> = { whiteSpace: 'nowrap' };

type CsvColumnAlignments = { [key: string]: 'left' | 'right' | 'center' | undefined };

export type CSVTableMetadata = {
  queryFileId?: string;
  timeRangeButtons?: TimeRangeButton[];
  align?: CsvColumnAlignments;
};

type CsvTableWidgetProps = {
  fileName: string;
  groupName: string;
  layout?: { fullWidth?: boolean; height?: number };
  metadata?: CSVTableMetadata;
};

type CsvTableContentProps = {
  from: string | undefined;
  fileName: string;
  groupName: string;
  queryFileId: string | undefined;
  columnAlignments: CsvColumnAlignments;
  layout: CsvTableWidgetProps['layout'];
};

const CsvTableContent = memo(
  ({ from, fileName, groupName, queryFileId, columnAlignments, layout }: CsvTableContentProps) => {
    const { userConfig } = useAppSelector(state => state.userConfig);
    const dateFormattingConfig = useMemo<DateFormattingConfig>(
      () => ({
        dateFormat: userConfig.dateFormat.value,
        timezone: userConfig.timezone.value
      }),
      [userConfig.dateFormat.value, userConfig.timezone.value]
    );

    const {
      data: rawText,
      isLoading,
      error
    } = useQuery(csvTableOptions.data({ fileName, groupName, queryFileId, from }));

    const [csvData, setCsvData] = useState<CsvTableData | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const workerRef = useRef<InstanceType<typeof CsvTableWorker> | null>(null);
    const requestIdRef = useRef(0);

    useEffect(() => {
      const worker = new CsvTableWorker();
      workerRef.current = worker;
      return () => {
        worker.terminate();
        workerRef.current = null;
      };
    }, []);

    useEffect(() => {
      if (!workerRef.current || rawText === undefined) return;
      if (!rawText.trim()) {
        setCsvData(null);
        setIsProcessing(false);
        return;
      }

      const id = ++requestIdRef.current;
      setIsProcessing(true);
      const worker = workerRef.current;
      worker.onmessage = (e: MessageEvent<CsvTableData>) => {
        if (id === requestIdRef.current) {
          setCsvData(e.data);
          setIsProcessing(false);
        }
      };
      worker.postMessage({
        text: rawText,
        userConfig: dateFormattingConfig
      } satisfies WorkerMessage);
    }, [rawText, dateFormattingConfig]);

    const { headerColumns, formattedDataRows, dateColumnKey } = csvData ?? {
      headerColumns: [],
      formattedDataRows: [],
      dateColumnKey: null
    };

    useCsvDownload(from, queryFileId, { fileName, groupName, dateColumnKey });

    const containerRef = useRef<HTMLDivElement>(null);
    const maxHeight = layout?.height
      ? layout.height * TABLE_ROW_HEIGHT_PX + TABLE_HEADER_HEIGHT_PX
      : Math.min(formattedDataRows.length, 20) * TABLE_ROW_HEIGHT_PX + TABLE_HEADER_HEIGHT_PX;

    const rowVirtualizer = useVirtualizer({
      count: formattedDataRows.length,
      getScrollElement: () => containerRef.current,
      estimateSize: () => TABLE_ROW_HEIGHT_PX,
      overscan: 5
    });

    const virtualRows = rowVirtualizer.getVirtualItems();
    const paddingTop = virtualRows[0]?.start ?? 0;
    const paddingBottom = Math.max(
      0,
      rowVirtualizer.getTotalSize() - (virtualRows.at(-1)?.end ?? 0)
    );

    const isRefreshing = isLoading || isProcessing;

    if (!csvData && isRefreshing) return <Loader />;

    if (!csvData || error || formattedDataRows.length === 0) return <EmptyState />;

    return (
      <Box sx={relativeWrapSx}>
        {isRefreshing && (
          <Box sx={overlaySx}>
            <CircularProgress size={32} />
          </Box>
        )}
        <TableContainer
          ref={containerRef}
          sx={tableContainerBaseSx}
          style={{ maxHeight }}
        >
          <Table
            stickyHeader
            size='small'
            sx={tableSx}
          >
            {headerColumns.length > 0 && (
              <TableHead sx={tableHeadSx}>
                <TableRow>
                  {headerColumns.map(column => (
                    <TableCell
                      key={column.id}
                      align={columnAlignments[column.name] ?? 'left'}
                    >
                      {column.name}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
            )}
            <TableBody>
              {paddingTop > 0 && (
                <TableRow>
                  <TableCell
                    colSpan={headerColumns.length}
                    sx={spacerCellSx}
                    style={{ height: paddingTop }}
                  />
                </TableRow>
              )}
              {virtualRows.map(virtualRow => {
                const row = formattedDataRows[virtualRow.index];
                return (
                  <TableRow
                    key={row.id}
                    data-striped={virtualRow.index % 2 === 1}
                    sx={tableRowSx}
                  >
                    {row.cells.map((cell, columnIndex) => {
                      const column = headerColumns[columnIndex];
                      return (
                        <TableCell
                          key={`${row.id}-${column.id}`}
                          align={columnAlignments[column.name] ?? 'left'}
                          sx={nowrapCellSx}
                        >
                          {cell}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
              {paddingBottom > 0 && (
                <TableRow>
                  <TableCell
                    colSpan={headerColumns.length}
                    sx={spacerCellSx}
                    style={{ height: paddingBottom }}
                  />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }
);

const CsvTableWidget = ({ fileName, groupName, metadata, layout }: CsvTableWidgetProps) => {
  const { hasPermission } = usePermission();
  const timeRangeButtons = useMemo(
    () => metadata?.timeRangeButtons ?? [],
    [metadata?.timeRangeButtons]
  );
  const columnAlignments = useMemo<CsvColumnAlignments>(
    () => metadata?.align ?? {},
    [metadata?.align]
  );
  const [from, setFrom] = useState<string | undefined>();
  const [activeButtonIndex, setActiveButtonIndex] = useState(0);
  const queryFileId = metadata?.queryFileId;
  const defaultButtonIndex = useMemo(
    () => timeRangeButtons.findIndex(button => button.default),
    [timeRangeButtons]
  );
  const effectiveFrom =
    from ?? (defaultButtonIndex >= 0 ? timeRangeButtons[defaultButtonIndex].value : undefined);
  const effectiveActiveButtonIndex =
    from === undefined && defaultButtonIndex >= 0 ? defaultButtonIndex : activeButtonIndex;

  const handleButtonClick = useCallback((value: string) => {
    startTransition(() => {
      setFrom(value);
    });
  }, []);

  if (!hasPermission(Permission.ViewMarketScreens)) {
    return (
      <Box
        sx={{
          flex: 1,
          height: '200px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <Typography sx={{ textAlign: 'center' }}>
          You no longer have access.
          <br />
          Please upgrade to access the full table.
        </Typography>
      </Box>
    );
  }

  return (
    <Paper>
      {timeRangeButtons.length > 0 && (
        <TimeRangeButtons
          activeButtonIndex={effectiveActiveButtonIndex}
          setActiveButtonIndex={setActiveButtonIndex}
          timeRangeButtons={timeRangeButtons}
          onButtonClick={handleButtonClick}
        />
      )}
      <CsvTableContent
        columnAlignments={columnAlignments}
        fileName={fileName}
        from={effectiveFrom}
        groupName={groupName}
        layout={layout}
        queryFileId={queryFileId}
      />
    </Paper>
  );
};

export default CsvTableWidget;
